import React, { useState, useEffect } from 'react';
import { Question, WritingSuggestion } from '../../../../types';
import { apiService } from '../../../../services';
import WritingSuggestions from '../WritingSuggestions';
import styles from './AIQuestions.module.css';

interface AIQuestionsProps {
  questions: Question[];
  initialAnswers?: Record<string, string>; // ← NEW: Initial answers from localStorage
  onAnswersChange: (answers: Record<string, string>) => void;
  industryId: string;
  roleId: string;
  onQuestionRegenerated?: (questionId: string, newQuestion: Question) => void;
}


const AIQuestions: React.FC<AIQuestionsProps> = ({
  questions,
  initialAnswers = {}, // ← Receive initial answers from parent
  onAnswersChange,
  industryId,
  roleId,
  onQuestionRegenerated
}) => {
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);
  const [regeneratingQuestion, setRegeneratingQuestion] = useState<string | null>(null);
  const [expandedHints, setExpandedHints] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // 🔥 CRITICAL: Initialize answers from initialAnswers prop (from localStorage)
    // This ensures answers are restored when navigating back to this step
    const mergedAnswers: Record<string, string> = {};
    questions.forEach((q: Question) => {
      // Priority: existing answer > initialAnswer > empty string
      mergedAnswers[q.id] = answers[q.id] || initialAnswers[q.id] || '';
    });
    setAnswers(mergedAnswers);
    
    console.log('📦 AIQuestions: Initialized answers', mergedAnswers);
  }, [questions, initialAnswers]); // Depend on both questions and initialAnswers


  const handleAnswerChange = (questionId: string, answer: string) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer
    };
    setAnswers(newAnswers);
    onAnswersChange(newAnswers);
  };

  const handleRegenerateQuestion = async (question: Question) => {
    try {
      setRegeneratingQuestion(question.id);
      console.log('Regenerating question:', question.id, question.text);
      
      const newQuestion = await apiService.regenerateSingleQuestion(
        industryId,
        roleId,
        question
      );
      
      console.log('New question received:', newQuestion.id, newQuestion.text);
      
      if (onQuestionRegenerated) {
        onQuestionRegenerated(question.id, newQuestion);
        console.log('Question regenerated callback called');
      }
      
    } catch (error) {
      console.error('Error regenerating question:', error);
      // You could add a toast notification here
    } finally {
      setRegeneratingQuestion(null);
    }
  };

  const getWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const getQuestionIcon = (category: string): string => {
    const iconMap: Record<string, string> = {
      'innovation_opportunities': 'lightbulb',
      'technology_adoption': 'laptop',
      'stakeholder_communication': 'comments',
      'resource_optimization': 'cogs',
      'risk_management': 'shield-alt',
      'customer_experience': 'user-friends',
      'operational_efficiency': 'tachometer-alt',
      'strategic_planning': 'chess',
      'team_collaboration': 'users',
      'performance_measurement': 'chart-line'
    };
    return iconMap[category] || 'question-circle';
  };


  const toggleHints = (questionId: string) => {
    setExpandedHints(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  if (questions.length === 0) {
    return (
      <div className={styles.noQuestions}>
        <i className="fas fa-info-circle"></i>
        <p>No hay preguntas generadas disponibles en este momento.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>
          <i className="fas fa-robot"></i>
          Preguntas Personalizadas con IA
        </h3>
        <p>Estas preguntas fueron creadas específicamente para tu rol e industria</p>
      </div>

      <div className={styles.questionsList}>
        {questions.map((question, index) => (
          <div key={question.id} className={styles.questionCard}>
            <div className={styles.questionHeader}>
              <div className={styles.questionNumber}>
                <i className={`fas fa-${getQuestionIcon(question.category)}`}></i>
                <span>Pregunta {index + 1}</span>
              </div>
              <div className={styles.questionActions}>
                <button
                  onClick={() => handleRegenerateQuestion(question)}
                  className={styles.regenerateButton}
                  disabled={regeneratingQuestion === question.id}
                  title="Regenerar pregunta"
                >
                  <i className={`fas fa-sync-alt ${regeneratingQuestion === question.id ? 'fa-spin' : ''}`}></i>
                  {regeneratingQuestion === question.id ? 'Generando...' : 'Regenerar'}
                </button>
              </div>
            </div>

            <div className={styles.questionText}>
              {question.text}
            </div>

            {question.hints && question.hints.length > 0 && (
              <div className={styles.hintsContainer}>
                <button 
                  className={styles.hintsToggle}
                  onClick={() => toggleHints(question.id)}
                >
                  <i className={`fas fa-lightbulb ${styles.hintIcon}`}></i>
                  <span>Sugerencias para responder</span>
                  <i className={`fas fa-chevron-${expandedHints[question.id] ? 'up' : 'down'}`}></i>
                </button>
                {expandedHints[question.id] && (
                  <div className={styles.hints}>
                    <ul>
                      {question.hints.map((hint: string, hintIndex: number) => (
                        <li key={hintIndex}>{hint}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className={styles.answerSection}>
              <label htmlFor={`answer-${question.id}`}>
                Tu Respuesta:
              </label>
              <textarea
                id={`answer-${question.id}`}
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder="Por favor proporciona una respuesta detallada..."
                className={styles.answerInput}
                rows={4}
              />
              
              <div className={styles.answerMeta}>
                <div className={styles.wordCount}>
                  <i className="fas fa-font"></i>
                  {getWordCount(answers[question.id] || '')} palabras
                  {question.minWords && (
                    <span className={styles.minWords}>
                      (recomendado: {question.minWords})
                    </span>
                  )}
                </div>
                {question.isRequired && (
                  <div className={styles.required}>
                    <i className="fas fa-asterisk"></i>
                    Requerido
                  </div>
                )}
              </div>

              {/* AI Writing Suggestions */}
              <WritingSuggestions
                questionId={question.id}
                industryId={industryId}
                roleId={roleId}
                currentAnswer={answers[question.id] || ''}
                onSuggestionClick={(suggestion: WritingSuggestion) => {
                  // Append suggestion content to current answer
                  const currentAnswer = answers[question.id] || '';
                  const newAnswer = currentAnswer 
                    ? `${currentAnswer}\n\n${suggestion.content}`
                    : suggestion.content;
                  handleAnswerChange(question.id, newAnswer);
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={styles.summary}>
        <p>
          <i className="fas fa-info-circle"></i>
          Has respondido {Object.values(answers).filter(answer => answer.trim().length > 0).length} de {questions.length} preguntas
        </p>
      </div>
    </div>
  );
};

export default AIQuestions;
